// Kịch bản mở đầu (STORY Phụ lục A, cảnh A1–A8; TONE §6.5). Khai bằng data: mỗi bước là một câu
// (khóa i18n `story.opening.<id>`), một kiểu thao tác và hiệu ứng khi qua bước. Cảnh A6b (chọn
// cơ duyên) thuộc M3; A10–A12 thuộc M2–M3.
import { BALANCE } from '../balance';
import { OPENING_FIRST_QUESTION_VALUES, type FlagValue } from '../flags';

export type Speaker = 'narration' | 'log' | 'servant' | 'steward' | 'lampSpirit';

export type OpeningInput =
  | { readonly kind: 'tap' }
  | { readonly kind: 'hold'; readonly labelKey: string }
  | {
      readonly kind: 'choice';
      readonly flag: string;
      readonly options: readonly { readonly value: string; readonly key: string }[];
    };

export interface OpeningEffects {
  readonly flags?: Readonly<Record<string, FlagValue>>;
  /** Đăng Căn thành: tu luyện bắt đầu (cờ `t1_dang_can_formed`). */
  readonly formLampRoot?: boolean;
  readonly codex?: readonly string[];
  /** Trao qua grantReward, khóa idempotent `quest.<id>`. */
  readonly reward?: { readonly questId: string; readonly spiritStone: number };
  /** Đăng Linh trao bộ khởi đầu (START_KIT), khóa idempotent `START_KIT_KEY`. */
  readonly startKit?: boolean;
}

/**
 * Bộ Đăng Linh trao trong phiên đầu (CRAFTING §2.6 ghi chú P0; §1.3 hạt khởi đầu): 3 Tụ Khí Đan,
 * 5 Hồi Xuân Đan, hạt Ngưng Lộ Thảo và Hoàng Tinh đủ cho mẻ Trúc Cơ Đan đầu.
 */
export const START_KIT: readonly { readonly stack: string; readonly amount: number }[] = [
  { stack: 'pill.qiGathering#mid', amount: BALANCE.GIFT_QI_PILLS },
  { stack: 'pill.springRevival#mid', amount: BALANCE.GIFT_SPRING_PILLS },
  { stack: 'seed.dewGrass', amount: 2 },
  { stack: 'seed.yellowEssence', amount: 2 },
];
export const START_KIT_KEY = 'gift.lampSpirit.startKit';

export interface OpeningStep {
  readonly id: string;
  readonly speaker: Speaker;
  readonly input: OpeningInput;
  readonly effects?: OpeningEffects;
}

const TAP = { kind: 'tap' } as const;

export const OPENING_STEPS: readonly OpeningStep[] = [
  { id: 'a1', speaker: 'log', input: TAP },
  { id: 'a1b', speaker: 'servant', input: TAP },
  { id: 'a1c', speaker: 'servant', input: TAP },
  { id: 'a2', speaker: 'narration', input: TAP },
  { id: 'a2b', speaker: 'servant', input: TAP },
  { id: 'a3', speaker: 'steward', input: TAP },
  { id: 'a4', speaker: 'narration', input: TAP },
  { id: 'a4b', speaker: 'log', input: { kind: 'hold', labelKey: 'story.opening.hold.lamp' } },
  { id: 'a5', speaker: 'lampSpirit', input: TAP, effects: { flags: { t1_lamp_lit: true } } },
  { id: 'a5b', speaker: 'lampSpirit', input: TAP },
  {
    id: 'a6',
    speaker: 'narration',
    input: {
      kind: 'choice',
      flag: 'main_opening_first_question',
      options: OPENING_FIRST_QUESTION_VALUES.map((v) => ({ value: v, key: `story.opening.a6.${v}` })),
    },
  },
  { id: 'a6b', speaker: 'lampSpirit', input: TAP },
  {
    id: 'a7',
    speaker: 'lampSpirit',
    input: { kind: 'hold', labelKey: 'story.opening.hold.qi' },
    effects: { formLampRoot: true, flags: { t1_dang_can_formed: true } },
  },
  { id: 'a7b', speaker: 'narration', input: TAP },
  { id: 'a7c', speaker: 'lampSpirit', input: TAP },
  {
    id: 'a8',
    speaker: 'log',
    input: TAP,
    effects: {
      codex: ['codex.lampSpirit'],
      reward: { questId: 'lk.clearHut', spiritStone: BALANCE.NEWBIE_QUEST_STONES },
      startKit: true,
    },
  },
];

/** Bước đã qua hết mở đầu. */
export const OPENING_END = 'end';

export function openingIndex(id: string): number {
  return OPENING_STEPS.findIndex((s) => s.id === id);
}
